import { cn } from "@/lib/utils";
import { Link, CloudDownload } from "lucide-react";
import { Attachment, useGetPublicUrl } from "@/api/attachment";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEffect, useState } from "react";
import { getAttachmentBiggestWeight } from "@/api/attachment";
import { Badge } from "@/components/ui/badge";

export const FileItemDisplay = ({
  file,
  className,
  }: {
  file: Attachment;
  className?: string;
}) => {

  const { data } = useGetPublicUrl(file.id);
  const { t } = useTranslation();

  const [biggestWeight, setBiggestWeight] = useState<number | null>(
    file.weight && file.weight > 0 ? file.weight : null,
  );

  useEffect(() => {
    if (!file?.id) return;

    let cancelled = false;
    void getAttachmentBiggestWeight(file.id).then((weight) => {
      if (cancelled || weight === null) return;
      setBiggestWeight(weight);
    });

    return () => {
      cancelled = true;
    };
  }, [file?.id, file?.weight]);

  const resolveShareUrl = (): string => {
    if (data?.data) return data.data;
    if (file.kind === "embed" && file.embedUrl) return file.embedUrl;
    if (file.kind === "video" && file.videoPublicUrl) return file.videoPublicUrl;
    return "";
  };

  const canDownload =
    file.kind === "embed"
      ? false
      : file.kind === "video"
        ? Boolean(file.videoPublicUrl)
        : Boolean(file.publicUrl);

  const copyAttachmentUrl = async () => {
    try {
      const url = resolveShareUrl();
      if (!url) return;
      navigator.clipboard.writeText(url);
      toast({
        variant: "success",
        description: t("copySuccess"),
      });
    } catch (error) {
      console.error("Error getting attachment URL:", error);
    }
  };

  const downloadAttachment = async () => {
    try {
      const url = resolveShareUrl();
      if (!url) {
        console.error("File share URL is null");
        return;
      }
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };
  return (
    <div className={cn("flex gap-2 items-center justify-between p-4 bg-[#F8FAFC]", className)}>
      <div className="flex w-full justify-between">
        <div className="flex items-center gap-2">
          {file.name && (
            <span>
              {file.name}
              {file.mimetype
                ? `.${file.mimetype.split("/")[1]}`
                : file.kind === "video"
                  ? ".mp4"
                  : ""}
            </span>
          )}
          {biggestWeight != null && biggestWeight > 0 && (
            <Badge variant="outlinedActive">{biggestWeight.toFixed(2)} Mo</Badge>
          )}
        </div>
        <div className="flex gap-6 items-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="bg-transparent" onClick={(e) => e.preventDefault()}>
              <Link
                className="w-5 h-5 text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => copyAttachmentUrl()}
              />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px] text-center text-[#6F6C90]">
                <p>{t("shareLinkTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {canDownload && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="bg-transparent" onClick={(e) => e.preventDefault()}>
                  <CloudDownload
                    className="w-5 h-5 text-gray-500 cursor-pointer hover:text-gray-700"
                    onClick={() => downloadAttachment()}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px] text-center text-[#6F6C90]">
                  <p>{t("downloadTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  )
}
